// ═══════════════════════════════════════════════════════════════
// CAPACITY CONFIDENCE SCORING
// Path: src/utils/capacityScoring.js
//
// Computes a 0–100 "confidence" score per donor that tells a
// development director how defensible their capacity estimate is.
// Six components, each scored 0–100, weighted and averaged.
//
// When a component has no possible data for this chapter (e.g. no
// wealth screen imports exist), its weight is silently redistributed
// across the remaining components so a confidence score of 100
// is always theoretically reachable.
//
// The component with the lowest score (and non-trivial weight)
// surfaces a "missing" action hint — this is the gap analysis
// that drives the director's next move.
// ═══════════════════════════════════════════════════════════════

// ── Capacity bands (matches donor_capacity CHECK constraint) ─────
export const CAPACITY_BANDS = [
  { id: "unknown",   label: "Unknown",           min: null, max: null },
  { id: "under_1k",  label: "Under $1,000",      min: 0, max: 1000 },
  { id: "1k_10k",    label: "$1K – $10K",        min: 1000, max: 10000 },
  { id: "10k_50k",   label: "$10K – $50K",       min: 10000, max: 50000 },
  { id: "50k_250k",  label: "$50K – $250K",      min: 50000, max: 250000 },
  { id: "250k_1m",   label: "$250K – $1M",       min: 250000, max: 1000000 },
  { id: "1m_plus",   label: "$1M+",              min: 1000000, max: null },
];

export const INCLINATION_OPTIONS = [
  { id: "cold",    label: "Cold",    color: "#94a3b8" },
  { id: "warm",    label: "Warm",    color: "#f59e0b" },
  { id: "hot",     label: "Hot",     color: "#ef4444" },
  { id: "ready",   label: "Ready",   color: "#10b981" },
  { id: "unknown", label: "Unknown", color: "#64748b" },
];

export const DIRECTOR_CONFIDENCE_OPTIONS = [
  { id: "low",    label: "Low" },
  { id: "medium", label: "Medium" },
  { id: "high",   label: "High" },
];

export const ESTIMATE_SOURCE_OPTIONS = [
  { id: "conversation",       label: "Direct conversation" },
  { id: "public_record",      label: "Public record research" },
  { id: "wealth_screen",      label: "Wealth screen" },
  { id: "director_intuition", label: "Director intuition" },
  { id: "unknown",            label: "Unspecified" },
];

// ── Default weights (sum = 1.0) ──────────────────────────────────
// These mirror the blueprint's 25/20/20/15/10/10.
export const DEFAULT_CAPACITY_WEIGHTS = {
  giving_history:     0.25,
  relationship_depth: 0.20,
  director_assessment: 0.20,
  external_wealth:    0.15,
  engagement_signal:  0.10,
  network_role:       0.10,
};

// ── Helpers ──────────────────────────────────────────────────────
const daysBetween = (a, b) =>
  Math.floor((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));

const clamp = (n, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, n));

// Step-scoring helper: given a value and an array of thresholds
// like [[0,0], [1,30], [3,60], [6,85], [12,100]], return the
// score for the largest threshold value that's ≤ the input.
const stepScore = (value, thresholds) => {
  let score = 0;
  for (const [threshold, s] of thresholds) {
    if (value >= threshold) score = s;
  }
  return score;
};

// ── Component scorers ────────────────────────────────────────────
// Each returns { score (0-100), missing (string|null) }.

const scoreGivingHistory = (gifts) => {
  const count = gifts?.length || 0;
  if (count === 0) {
    return {
      score: 0,
      missing: "No giving history — a first gift is the strongest capacity signal.",
    };
  }
  const countScore = stepScore(count, [
    [0, 0], [1, 25], [2, 45], [4, 65], [8, 85], [16, 100],
  ]);
  const now = new Date();
  const lastGiftDate = gifts[0]?.gift_date || gifts[0]?.giftDate;
  const recencyScore = lastGiftDate
    ? (() => {
        const d = daysBetween(new Date(lastGiftDate + "T12:00:00"), now);
        if (d <= 365) return 100;
        if (d <= 730) return 70;
        if (d <= 1095) return 40;
        return 20;
      })()
    : 0;
  const firstGiftDate = gifts[gifts.length - 1]?.gift_date || gifts[gifts.length - 1]?.giftDate;
  const tenureYears = firstGiftDate
    ? daysBetween(new Date(firstGiftDate + "T12:00:00"), now) / 365
    : 0;
  const tenureScore = stepScore(tenureYears, [
    [0, 0], [0.5, 25], [1, 45], [2, 65], [4, 85], [8, 100],
  ]);
  const score = Math.round((countScore + recencyScore + tenureScore) / 3);
  return {
    score,
    missing:
      score < 50
        ? "Giving history is light — any recent gift will strengthen this signal substantially."
        : null,
  };
};

const scoreRelationshipDepth = (contactLogs, contactReports) => {
  const now = new Date();
  const twelveMonthsAgo = new Date(now);
  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
  const logsRecent = (contactLogs || []).filter((log) => {
    const d = log.log_date || log.logDate;
    if (!d) return false;
    return new Date(d) >= twelveMonthsAgo;
  }).length;
  const logsScore = stepScore(logsRecent, [
    [0, 0], [1, 30], [3, 55], [6, 80], [12, 100],
  ]);
  const reportsCount = (contactReports || []).length;
  const reportsScore = stepScore(reportsCount, [
    [0, 0], [1, 60], [3, 100],
  ]);
  const score = Math.round((logsScore * 0.6) + (reportsScore * 0.4));
  return {
    score,
    missing:
      reportsCount === 0 && logsRecent < 3
        ? "Log a substantive contact — the fastest way to raise this score."
        : reportsCount === 0
        ? "File a contact report after your next meeting to deepen this signal."
        : null,
  };
};

const scoreDirectorAssessment = (capacity) => {
  if (!capacity) {
    return {
      score: 0,
      missing: "Enter your capacity estimate — your judgment is load-bearing here.",
    };
  }
  let score = 0;
  if (capacity.capacityBand && capacity.capacityBand !== "unknown") score += 40;
  if (capacity.directorConfidence === "low") score += 10;
  if (capacity.directorConfidence === "medium") score += 25;
  if (capacity.directorConfidence === "high") score += 40;
  if (capacity.inclination && capacity.inclination !== "unknown") score += 10;
  if ((capacity.narrativeNotes || "").trim().length > 20) score += 20;
  score = clamp(score);
  return {
    score,
    missing:
      score < 40
        ? "Director assessment is thin — set a capacity band and a confidence level."
        : !(capacity.narrativeNotes || "").trim()
        ? "Add a narrative note explaining what you know about this donor's capacity."
        : null,
  };
};

const scoreExternalWealth = (wealthProfile) => {
  if (!wealthProfile) {
    return {
      score: 0,
      missing: null, // handled by rebalance when chapter has no imports
    };
  }
  let score = 40;
  if (wealthProfile.capacityRating) score += 20;
  if (wealthProfile.realEstateValue) score += 15;
  if (wealthProfile.stockValue) score += 10;
  if (wealthProfile.politicalGivingTotal) score += 10;
  if (wealthProfile.foundationAffiliations?.length) score += 10;
  return {
    score: clamp(score),
    missing:
      score < 50
        ? "Wealth profile is incomplete — a fresh DonorSearch pull would fill the gaps."
        : null,
  };
};

const scoreEngagementSignal = (member, engagementScore) => {
  if (engagementScore == null) {
    return { score: 0, missing: "No engagement score yet." };
  }
  let score;
  if (engagementScore >= 80) score = 100;
  else if (engagementScore >= 60) score = 75;
  else if (engagementScore >= 40) score = 50;
  else if (engagementScore >= 20) score = 25;
  else score = 10;
  return {
    score,
    missing:
      score < 40
        ? "Engagement is low — an event invite or personal touch can lift this."
        : null,
  };
};

const scoreNetworkRole = (member) => {
  let score = 0;
  const flags = {
    isBoard: member?.isBoard || member?.is_board,
    isCommittee: member?.isCommittee || member?.is_committee,
    personaTag: member?.persona || member?.personaId,
    orgAffiliation: member?.organization,
  };
  if (flags.isBoard) score += 60;
  if (flags.isCommittee) score += 20;
  if (flags.personaTag) score += 15;
  if (flags.orgAffiliation) score += 10;
  score = clamp(score);
  return {
    score,
    missing:
      score < 30
        ? "No network role markers — no board/committee service, persona tag, or org affiliation on file."
        : null,
  };
};

// ── Rebalance helper ─────────────────────────────────────────────
// When a component has "no possible signal" for this chapter (e.g.
// external wealth when nothing has ever been imported), drop it
// and redistribute its weight across the others so the overall
// score isn't artificially depressed.
const rebalance = (rawWeights, exclude) => {
  const kept = {};
  let excludedWeight = 0;
  Object.keys(rawWeights).forEach((key) => {
    if (exclude.has(key)) {
      excludedWeight += rawWeights[key];
    } else {
      kept[key] = rawWeights[key];
    }
  });
  const keptSum = Object.values(kept).reduce((a, b) => a + b, 0);
  if (keptSum === 0) return rawWeights;
  const normalized = {};
  Object.keys(kept).forEach((key) => {
    normalized[key] = kept[key] / keptSum;
  });
  return normalized;
};

// ═══════════════════════════════════════════════════════════════
// Main entry point
// ═══════════════════════════════════════════════════════════════
export const computeCapacityConfidence = ({
  member,
  capacity,
  contactLogs = [],
  contactReports = [],
  gifts = [],
  engagementScore = null,
  wealthProfile = null,
  weights: overrideWeights = null,
  chapterHasWealthData = false,
}) => {
  const baseWeights = { ...DEFAULT_CAPACITY_WEIGHTS, ...(overrideWeights || {}) };

  // Silent rebalance: drop external_wealth if this chapter has no
  // wealth imports at all. Prevents a permanent 15% ceiling for
  // small shops that don't pay for DonorSearch.
  const excluded = new Set();
  if (!chapterHasWealthData && !wealthProfile) {
    excluded.add("external_wealth");
  }
  const weights = rebalance(baseWeights, excluded);

  const componentScorers = {
    giving_history: {
      label: "Giving history depth",
      result: scoreGivingHistory(gifts),
    },
    relationship_depth: {
      label: "Relationship depth",
      result: scoreRelationshipDepth(contactLogs, contactReports),
    },
    director_assessment: {
      label: "Director assessment",
      result: scoreDirectorAssessment(capacity),
    },
    external_wealth: {
      label: "External wealth data",
      result: scoreExternalWealth(wealthProfile),
    },
    engagement_signal: {
      label: "Engagement signal",
      result: scoreEngagementSignal(member, engagementScore),
    },
    network_role: {
      label: "Network & role",
      result: scoreNetworkRole(member),
    },
  };

  const components = Object.keys(componentScorers).map((key) => {
    const included = !excluded.has(key);
    const weight = included ? weights[key] || 0 : 0;
    const baseWeight = baseWeights[key] || 0;
    const { result, label } = { ...componentScorers[key], label: componentScorers[key].label };
    return {
      key,
      label,
      score: result.score,
      weight,
      baseWeight,
      included,
      missing: included ? result.missing : null,
    };
  });

  const overall = Math.round(
    components.reduce(
      (sum, c) => (c.included ? sum + c.score * c.weight : sum),
      0
    )
  );

  // Gap analysis: the lowest-scoring included component with a
  // non-null missing hint is the top "next thing to do".
  const gaps = components
    .filter((c) => c.included && c.missing && c.score < 70)
    .sort((a, b) => a.score * a.weight - b.score * b.weight)
    .slice(0, 3);

  return {
    score: clamp(overall),
    components,
    gaps,
    wealthDataAvailable: !excluded.has("external_wealth"),
  };
};

export const getCapacityBand = (bandId) =>
  CAPACITY_BANDS.find((b) => b.id === bandId) || null;

// Dollar midpoint for a capacity band, used by "opportunity gap"
// calculations in CapacityView. $1M+ is anchored at $2M since it's
// unbounded — a defensible default for prospect-level asks.
export const BAND_MIDPOINTS = {
  unknown: null,
  under_1k: 500,
  "1k_10k": 5500,
  "10k_50k": 30000,
  "50k_250k": 150000,
  "250k_1m": 625000,
  "1m_plus": 2000000,
};

export const getBandMidpoint = (bandId) =>
  BAND_MIDPOINTS[bandId] != null ? BAND_MIDPOINTS[bandId] : null;

// 5-year giving total for a member, used in the opportunity gap
// calculation. Takes a flat gifts array and filters by date.
export const computeFiveYearGiving = (gifts = [], referenceDate = new Date()) => {
  const cutoff = new Date(referenceDate);
  cutoff.setFullYear(cutoff.getFullYear() - 5);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return gifts.reduce((sum, g) => {
    const d = g.gift_date || g.giftDate;
    if (!d || d < cutoffStr) return sum;
    return sum + (Number(g.amount) || 0);
  }, 0);
};

// Infer a suggested capacity band from 5-year giving. The industry
// heuristic is that donors give 5–10% of capacity annually, so a
// 5-year giving total should sit around 1/5 of real 5-year capacity.
// We use a 5x multiplier as a conservative floor — the director's
// assessment always overrides this, but without it we have *something*
// to drive the portfolio triage on day one.
export const suggestBandFromGiving = (fiveYearGiving) => {
  if (!fiveYearGiving || fiveYearGiving <= 0) return "unknown";
  const inferredCapacity = fiveYearGiving * 5;
  if (inferredCapacity < 1000) return "under_1k";
  if (inferredCapacity < 10000) return "1k_10k";
  if (inferredCapacity < 50000) return "10k_50k";
  if (inferredCapacity < 250000) return "50k_250k";
  if (inferredCapacity < 1000000) return "250k_1m";
  return "1m_plus";
};

// Returns the band we should *use* for portfolio-view calculations.
// Priority: director-assigned band > suggestion from giving > unknown.
export const resolveEffectiveBand = (capacityRow, fiveYearGiving) => {
  const assigned =
    capacityRow?.capacityBand && capacityRow.capacityBand !== "unknown"
      ? capacityRow.capacityBand
      : null;
  if (assigned) return { bandId: assigned, source: "assigned" };
  const suggested = suggestBandFromGiving(fiveYearGiving);
  return {
    bandId: suggested,
    source: suggested === "unknown" ? "none" : "suggested",
  };
};

// The headline metric: capacity band midpoint minus 5-year giving.
// Returns null when capacity is unknown/unset — can't compute a gap
// without an estimate. Negative gaps are clamped to 0 (donor is at
// or above rated capacity — no "upside" to chase here).
export const computeOpportunityGap = (band, fiveYearGiving) => {
  const midpoint = getBandMidpoint(band);
  if (midpoint == null) return null;
  return Math.max(0, midpoint - (fiveYearGiving || 0));
};
